// src/pages/PortalInvoiceDetailPage.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import portalApi from '../utils/portalApi';
import toast from 'react-hot-toast';

export default function PortalInvoiceDetailPage() {
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    portalApi.get(`/portal/invoices/${id}`)
      .then(res => setInvoice(res.data))
      .catch(() => toast.error('Failed to load invoice.'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="loading-page"><div className="spinner" /></div>;
  if (!invoice) return <div className="empty-state"><h3>Invoice not found</h3><Link to="/portal/invoices">← Back</Link></div>;

  const balance = parseFloat(invoice.balance_due || 0);
  const isPayable = !['paid', 'void'].includes(invoice.status) && balance > 0;

  return (
    <div>
      <div className="page-header">
        <div>
          <div style={{ fontSize: '0.8rem', color: 'var(--gray-400)', marginBottom: 4 }}>
            <Link to="/portal/invoices" style={{ color: 'var(--gray-400)' }}>Invoices</Link> / {invoice.invoice_number}
          </div>
          <h1 className="page-title td-mono" style={{ fontSize: '1.4rem' }}>{invoice.invoice_number}</h1>
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            <span className={`badge badge-${invoice.status}`}>{invoice.status?.replace('_', ' ')}</span>
            <span style={{ textTransform: 'capitalize', color: 'var(--gray-500)', fontSize: '0.85rem' }}>
              {invoice.invoice_type?.replace('_', ' ')}
            </span>
          </div>
        </div>
        {isPayable && invoice.cardknox_payment_link && (
          <a href={invoice.cardknox_payment_link} target="_blank" rel="noreferrer"
             className="btn btn-primary btn-lg">
            Pay ${balance.toFixed(2)}
          </a>
        )}
      </div>

      <div className="stat-grid" style={{ marginBottom: 20 }}>
        <div className="stat-card">
          <div className="stat-label">Total</div>
          <div className="stat-value">${parseFloat(invoice.total_amount).toFixed(2)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Paid</div>
          <div className="stat-value" style={{ color: 'var(--success)' }}>${parseFloat(invoice.amount_paid).toFixed(2)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Balance Due</div>
          <div className="stat-value" style={{ color: balance > 0 ? 'var(--danger)' : 'var(--success)' }}>
            ${balance.toFixed(2)}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header"><span className="card-title">Details</span></div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, fontSize: '0.875rem' }}>
            <div><strong style={{ color: 'var(--gray-500)' }}>Issued:</strong> {fmtDate(invoice.issue_date)}</div>
            <div><strong style={{ color: 'var(--gray-500)' }}>Due:</strong> {fmtDate(invoice.due_date)}</div>
            {invoice.paid_at && (
              <div><strong style={{ color: 'var(--gray-500)' }}>Paid:</strong> {fmtDate(invoice.paid_at)}</div>
            )}
          </div>
          {invoice.notes && (
            <div style={{ marginTop: 16, fontSize: '0.875rem' }}>
              <strong style={{ color: 'var(--gray-500)' }}>Notes:</strong>
              <p style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>{invoice.notes}</p>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header"><span className="card-title">Line Items</span></div>
        <div className="table-wrap" style={{ border: 'none' }}>
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th style={{ textAlign: 'right' }}>Unit Price</th>
                <th style={{ textAlign: 'right' }}>Qty</th>
                <th style={{ textAlign: 'right' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {(invoice.lineItems || []).map((it, i) => (
                <tr key={i}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{it.name}</div>
                    {it.description && <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>{it.description}</div>}
                  </td>
                  <td style={{ textAlign: 'right' }}>${parseFloat(it.unit_price).toFixed(2)}</td>
                  <td style={{ textAlign: 'right' }}>{parseFloat(it.quantity).toString()}</td>
                  <td style={{ textAlign: 'right' }}>${parseFloat(it.line_total).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {isPayable && invoice.cardknox_payment_link && (
        <div style={{ marginTop: 20, padding: 16, background: 'var(--gray-50)', borderRadius: 6, textAlign: 'center' }}>
          <p style={{ marginBottom: 12, color: 'var(--gray-600)' }}>
            Payment is processed securely by Cardknox in a new tab.
          </p>
          <a href={invoice.cardknox_payment_link} target="_blank" rel="noreferrer"
             className="btn btn-primary btn-lg">
            Pay ${balance.toFixed(2)} Now
          </a>
        </div>
      )}
    </div>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
